import { useEffect, useRef } from 'react';
import Chart, { formatAmount, niceMax, useBoxWidth, withUnit, xLabelIndexes } from './Chart';
import { Bars } from './DayBars';
import Skeleton from './Skeleton';

// §2.6 (rev 2.3) SmallMultiples: one small DayBars per person, every one on the same scale, so heights compare across the
// row. Each carries the person's name and their total for the range (t-mono); no y labels, the target rule as in DayBars.
//   items: [{ key, name, points: [{ key, label, amount, target }] }]; upTo, unit as DayBars.
// HD-11.1 (lazy): an item may come with points: null and onVisible(); it draws a skeleton of the chart's size and calls
// onVisible once when it scrolls near the viewport (the page fetches its series then). Its total and target come from
// item.amount / item.target until the points arrive, so the table keeps every row. The shared scale is the loaded charts'.
function Lazy({ item }) {
  const ref = useRef(null);
  const asked = useRef(false);
  useEffect(() => {
    const el = ref.current; if (!el || !item.onVisible) return undefined;
    const ask = () => { if (!asked.current) { asked.current = true; item.onVisible(); } };
    if (typeof IntersectionObserver === 'undefined') { ask(); return undefined; }
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { ask(); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [item]);
  return (
    <div className="ui-multi__one" ref={ref} data-testid="small-multiple-loading">
      <h4 className="ui-multi__name"><span className="ui-truncate" title={item.name}>{item.name}</span></h4>
      <div className="ui-chart__plot"><Skeleton variant="block" height={120} label={`Loading ${item.name}`} /></div>
    </div>
  );
}

function One({ item, upTo, unit, max }) {
  const [ref, w] = useBoxWidth(240);
  const W = Math.max(w, 120), H = 120, pl = 4, pr = 4, pt = 12, pb = 20;
  const total = item.points.slice(0, upTo + 1).reduce((s, p) => s + (p.amount || 0), 0);
  // Letters for a week of days, otherwise the first and last label only: a small chart has no room for a date axis.
  const n = item.points.length;
  const labels = n <= 7 ? xLabelIndexes(n, W - pl - pr, 20) : new Set([0, n - 1]);
  const pts = n <= 7 ? item.points.map(p => ({ ...p, axis: p.short || p.axis })) : item.points;
  return (
    <div className="ui-multi__one">
      <h4 className="ui-multi__name"><span className="ui-truncate" title={item.name}>{item.name}</span><span className="ui-multi__total ui-num">{formatAmount(total, unit)}</span></h4>
      <div className="ui-chart__plot" ref={ref}>
        <Bars points={pts} upTo={upTo} unit={unit} W={W} H={H} pl={pl} pr={pr} pt={pt} pb={pb} max={max} labels={labels} compact
          label={`${item.name}: ${withUnit(total, unit)}.`} />
      </div>
    </div>
  );
}

export default function SmallMultiples({ title, note, items, upTo, unit = 'words' }) {
  const loaded = items.filter(i => i.points);
  const last = upTo ?? Math.max(0, ...loaded.map(i => i.points.length - 1));
  const max = niceMax(Math.max(0, ...loaded.flatMap(i => i.points.map(p => Math.max(p.amount || 0, p.target || 0)))));
  const rows = items.map(i => {
    if (!i.points) return { key: i.key, name: i.name, amount: i.amount ?? 0, target: i.target ?? null, met: undefined, targeted: 0 };
    const pts = i.points.slice(0, last + 1);
    const amount = pts.reduce((s, p) => s + (p.amount || 0), 0);
    const hasT = pts.some(p => p.target > 0);
    return { key: i.key, name: i.name, amount, target: hasT ? pts.reduce((s, p) => s + (p.target || 0), 0) : null,
      met: hasT ? pts.filter(p => p.target > 0 && (p.amount || 0) >= p.target).length : null, targeted: pts.filter(p => p.target > 0).length };
  });
  const table = {
    columns: [
      { key: 'name', label: 'Person' },
      { key: 'amount', label: 'Approved', align: 'right', render: r => <span className="ui-num">{formatAmount(r.amount, unit)}</span> },
      { key: 'target', label: 'Target', align: 'right', render: r => (r.target != null ? <span className="ui-num">{formatAmount(r.target, unit)}</span> : <span className="ui-text-3">None</span>) },
      { key: 'met', label: 'Days at target', align: 'right', render: r => (r.met === undefined ? <span className="ui-text-3">Loading</span> : r.met != null ? <span className="ui-num">{r.met} of {r.targeted}</span> : <span className="ui-text-3">None</span>) },
    ],
    rows, rowKey: r => r.key,
  };
  return (
    <Chart title={title} note={note} table={table}>
      <div className="ui-multi">
        {items.map(i => (i.points ? <One key={i.key} item={i} upTo={last} unit={unit} max={max} /> : <Lazy key={i.key} item={i} />))}
      </div>
    </Chart>
  );
}
